/**
 * CustomerTrackingDashboard.tsx
 * 
 * This component provides a comprehensive dashboard for customers to track their shipments.
 * It displays real-time tracking information, shipment details, and verification status.
 * 
 * Key features:
 * - Real-time shipment tracking with map integration
 * - Shipment status updates and history
 * - Driver and vehicle verification information
 * - Traffic and ETA updates
 * - Environmental monitoring for sensitive cargo
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Divider,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Badge,
  Tooltip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Checkbox,
  ListItemIcon,
} from '@mui/material';
import {
  LocalShipping as TruckIcon,
  LocationOn as LocationIcon,
  MyLocation as MyLocationIcon,
  Navigation as NavigationIcon,
  Speed as SpeedIcon,
  Timer as TimerIcon,
  Traffic as TrafficIcon,
  Phone as PhoneIcon,
  Message as MessageIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Share as ShareIcon,
  Map as MapIcon,
  Refresh as RefreshIcon,
  Route as RouteIcon,
  AccessTime as TimeIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
  Notifications as NotificationsIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  CalendarToday as CalendarIcon,
  DirectionsWalk as WalkIcon,
  DirectionsCar as CarIcon,
  Thermostat as TemperatureIcon,
  VerifiedUser as VerifiedUserIcon,
  Person as PersonIcon,
} from '@mui/icons-material';

import ShipmentTrackingMap from './ShipmentTrackingMap';

// Interface for shipment data
interface Shipment {
  id: string;
  status: 'pending' | 'in-transit' | 'delivered' | 'delayed';
  trackingNumber: string;
  origin: {
    name: string;
    coordinates: {
      lat: number;
      lng: number;
    };
  };
  destination: {
    name: string;
    coordinates: {
      lat: number;
      lng: number;
    };
  };
  currentLocation?: {
    address?: string;
    coordinates: {
      lat: number;
      lng: number;
    };
    updatedAt: string;
    speed: number;
  };
  estimatedDelivery: {
    originalDate?: string;
    date: string;
    timeWindow: string;
    eta?: string; // Estimated time of arrival
  };
  actualDelivery?: {
    date: string;
    time: string;
    signature?: string;
  };
  driver: {
    id: string;
    name: string;
    phone: string;
    avatar?: string;
    rating: number;
    verified: boolean;
    insuranceVerified: boolean;
  };
  vehicle: {
    type: string;
    registrationNumber: string;
    insuranceVerified: boolean;
  };
  route: {
    alternateRoutes?: Array<{
      distance: number;
      duration: number;
      trafficCondition: 'light' | 'moderate' | 'heavy';
    }>;
    distance: number;
    duration: number;
    trafficCondition: 'light' | 'moderate' | 'heavy';
    waypoints: Array<{
      name: string;
      coordinates: {
        lat: number;
        lng: number;
      };
      arrivalTime?: string;
      departureTime?: string;
      status: 'pending' | 'arrived' | 'departed';
    }>;
  };
  trafficUpdates?: Array<{
    timestamp: string;
    condition: 'light' | 'moderate' | 'heavy';
    location: string;
    impact: 'none' | 'minor' | 'significant' | 'severe';
    delayMinutes?: number;
  }>;
  updates: Array<{
    timestamp: string;
    message: string;
    type: 'info' | 'warning' | 'success';
  }>;
  cargo: {
    type: string;
    quantity: string;
    weight: string;
    specialHandling?: string[];
  };
  environmentalData?: {
    temperature?: number;
    humidity?: number;
    lastUpdated: string;
  };
}

// Interface for the component props
interface CustomerTrackingDashboardProps {
  userId?: string;
}

/**
 * CustomerTrackingDashboard component for customers to track their shipments
 * Provides real-time tracking, traffic updates, and ETA information
 * Displays verification status of drivers and vehicles
 * @param userId - ID of the current user
 * @returns React component for customer tracking dashboard
 */
const CustomerTrackingDashboard: React.FC<CustomerTrackingDashboardProps> = ({
  userId,
}) => {
  // State for active tab
  const [activeTab, setActiveTab] = useState(0);
  
  // State for shipments
  const [shipments, setShipments] = useState<Shipment[]>([]);
  
  // State for selected shipment
  const [selectedShipment, setSelectedShipment] = useState<Shipment | null>(null);
  
  // State for tracking number input
  const [trackingNumber, setTrackingNumber] = useState('');
  
  // State for loading status
  const [loading, setLoading] = useState(false);
  
  // State for error messages
  const [error, setError] = useState<string | null>(null);
  
  // State for success messages
  const [success, setSuccess] = useState<string | null>(null);
  
  // State for tracking dialog
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  
  // State for filter options
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // State for verification details dialog
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  const [verificationDetails, setVerificationDetails] = useState<{
    driverVerified: boolean;
    vehicleInsuranceVerified: boolean;
    driverInsuranceVerified: boolean;
  }>({ driverVerified: false, vehicleInsuranceVerified: false, driverInsuranceVerified: false });

  /**
   * Fetch shipments for the current user
   */
  useEffect(() => {
    fetchShipments();
  }, []);

  /**
   * Fetch shipments from the API
   */
  const fetchShipments = () => {
    setLoading(true);
    setError(null);
    
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      
      // Mock data for demonstration
      const mockShipments: Shipment[] = [
        {
          id: '1',
          status: 'in-transit',
          trackingNumber: 'KE12345678',
          origin: {
            name: 'Nakuru Farmers Cooperative',
            coordinates: {
              lat: -0.3031,
              lng: 36.0800,
            },
          },
          destination: {
            name: 'Nairobi Central Market',
            coordinates: {
              lat: -1.2921,
              lng: 36.8219,
            },
          },
          currentLocation: {
            address: 'Naivasha Road, near Lake Naivasha',
            coordinates: {
              lat: -0.7134,
              lng: 36.4316,
            },
            updatedAt: new Date().toISOString(),
            speed: 65,
          },
          estimatedDelivery: {
            originalDate: '2023-06-15',
            date: '2023-06-15',
            timeWindow: '14:00 - 16:00',
            eta: '15:30',
          },
          driver: {
            id: 'd1',
            name: 'John Kamau',
            phone: '+254712345678',
            rating: 4.8,
            verified: true,
            insuranceVerified: true,
          },
          vehicle: {
            type: 'Refrigerated Truck',
            registrationNumber: 'KBZ 123A',
            insuranceVerified: true,
          },
          route: {
            distance: 158,
            duration: 180,
            trafficCondition: 'moderate',
            waypoints: [
              {
                name: 'Naivasha Checkpoint',
                coordinates: {
                  lat: -0.7134,
                  lng: 36.4316,
                },
                status: 'departed',
                arrivalTime: '12:15',
                departureTime: '12:30',
              },
              {
                name: 'Limuru Weighbridge',
                coordinates: {
                  lat: -1.1073,
                  lng: 36.6526,
                },
                status: 'pending',
              },
            ],
            alternateRoutes: [
              {
                distance: 175,
                duration: 195,
                trafficCondition: 'light',
              },
            ],
          },
          trafficUpdates: [
            {
              timestamp: new Date(Date.now() - 30 * 60000).toISOString(),
              condition: 'heavy',
              location: 'Nakuru Highway near Naivasha',
              impact: 'significant',
              delayMinutes: 25,
            },
            {
              timestamp: new Date(Date.now() - 15 * 60000).toISOString(),
              condition: 'moderate',
              location: 'Approaching Limuru',
              impact: 'minor',
              delayMinutes: 10,
            },
          ],
          updates: [
            {
              timestamp: new Date(Date.now() - 120 * 60000).toISOString(),
              message: 'Shipment has been picked up from Nakuru Farmers Cooperative',
              type: 'info',
            },
            {
              timestamp: new Date(Date.now() - 60 * 60000).toISOString(),
              message: 'Shipment has passed Naivasha checkpoint',
              type: 'info',
            },
            {
              timestamp: new Date(Date.now() - 30 * 60000).toISOString(),
              message: 'Heavy traffic encountered, taking alternate route',
              type: 'warning',
            },
          ],
          cargo: {
            type: 'Fresh Produce',
            quantity: '2 tons',
            weight: '2000 kg',
            specialHandling: ['Refrigerated', 'Fragile'],
          },
          environmentalData: {
            temperature: 4.5,
            humidity: 85,
            lastUpdated: new Date().toISOString(),
          },
        },
        {
          id: '2',
          status: 'pending',
          trackingNumber: 'KE23456789',
          origin: {
            name: 'Meru Farmers Association',
            coordinates: {
              lat: 0.0500,
              lng: 37.6500,
            },
          },
          destination: {
            name: 'Mombasa Port',
            coordinates: {
              lat: -4.0435,
              lng: 39.6682,
            },
          },
          estimatedDelivery: {
            date: '2023-06-18',
            timeWindow: '09:00 - 12:00',
          },
          driver: {
            id: 'd2',
            name: 'David Ochieng',
            phone: '+254723456789',
            rating: 4.5,
            verified: true,
            insuranceVerified: true,
          },
          vehicle: {
            type: 'Flatbed Truck',
            registrationNumber: 'KCY 456B',
            insuranceVerified: true,
          },
          route: {
            distance: 580,
            duration: 480,
            trafficCondition: 'light',
            waypoints: [],
          },
          updates: [
            {
              timestamp: new Date(Date.now() - 24 * 60 * 60000).toISOString(),
              message: 'Shipment has been scheduled for pickup tomorrow',
              type: 'info',
            },
          ],
          cargo: {
            type: 'Coffee Beans',
            quantity: '5 tons',
            weight: '5000 kg',
            specialHandling: ['Dry Storage'],
          },
        },
        {
          id: '3',
          status: 'delivered',
          trackingNumber: 'KE34567890',
          origin: {
            name: 'Eldoret Grain Stores',
            coordinates: {
              lat: 0.5143,
              lng: 35.2698,
            },
          },
          destination: {
            name: 'Nairobi Grain Terminal',
            coordinates: {
              lat: -1.3182,
              lng: 36.8959,
            },
          },
          currentLocation: {
            address: 'Nairobi Grain Terminal',
            coordinates: {
              lat: -1.3182,
              lng: 36.8959,
            },
            updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60000).toISOString(),
            speed: 0,
          },
          estimatedDelivery: {
            originalDate: '2023-06-10',
            date: '2023-06-10',
            timeWindow: '10:00 - 12:00',
          },
          actualDelivery: {
            date: '2023-06-10',
            time: '11:23',
            signature: 'James Mwangi',
          },
          driver: {
            id: 'd3',
            name: 'Samuel Kipchoge',
            phone: '+254734567890',
            rating: 4.9,
            verified: true,
            insuranceVerified: true,
          },
          vehicle: {
            type: 'Bulk Carrier',
            registrationNumber: 'KDG 789C',
            insuranceVerified: true,
          },
          route: {
            distance: 320,
            duration: 360,
            trafficCondition: 'moderate',
            waypoints: [],
          },
          updates: [
            {
              timestamp: new Date(Date.now() - 4 * 24 * 60 * 60000).toISOString(),
              message: 'Shipment has been picked up from Eldoret Grain Stores',
              type: 'info',
            },
            {
              timestamp: new Date(Date.now() - 3 * 24 * 60 * 60000).toISOString(),
              message: 'Shipment is in transit to Nairobi',
              type: 'info',
            },
            {
              timestamp: new Date(Date.now() - 2 * 24 * 60 * 60000).toISOString(),
              message: 'Shipment has been delivered to Nairobi Grain Terminal',
              type: 'success',
            },
          ],
          cargo: {
            type: 'Maize',
            quantity: '10 tons',
            weight: '10000 kg',
            specialHandling: ['Moisture Control'],
          },
        },
      ];
      
      setShipments(mockShipments);
    }, 1000);
  };

  /**
   * Handle tab change
   * @param event - The event object
   * @param newValue - The new tab index
   */
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  /**
   * Track a shipment by tracking number
   */
  const handleTrackShipment = () => {
    if (!trackingNumber) {
      setError('Please enter a tracking number');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      
      const foundShipment = shipments.find(s => s.trackingNumber === trackingNumber);
      
      if (foundShipment) {
        setSelectedShipment(foundShipment);
        setTrackingDialogOpen(true);
        setSuccess(`Shipment ${trackingNumber} found`);
      } else {
        setError(`No shipment found with tracking number ${trackingNumber}`);
      }
    }, 1000);
  };

  /**
   * Open the tracking dialog for a shipment
   * @param shipment - The shipment to track
   */
  const handleOpenTrackingDialog = (shipment: Shipment) => {
    setSelectedShipment(shipment);
    setTrackingDialogOpen(true);
  };

  /**
   * Close the tracking dialog
   */
  const handleCloseTrackingDialog = () => {
    setTrackingDialogOpen(false);
  };

  /**
   * Open the verification details dialog
   * @param shipment - The shipment with verification details
   */
  const handleOpenVerificationDialog = (shipment: Shipment) => {
    setVerificationDetails({
      driverVerified: shipment.driver.verified,
      vehicleInsuranceVerified: shipment.vehicle.insuranceVerified,
      driverInsuranceVerified: shipment.driver.insuranceVerified,
    });
    setVerificationDialogOpen(true);
  };

  /**
   * Close the verification details dialog
   */
  const handleCloseVerificationDialog = () => {
    setVerificationDialogOpen(false);
  };

  /**
   * Filter shipments by status
   */
  const filteredShipments = shipments.filter(shipment => {
    if (filterStatus === 'all') return true;
    return shipment.status === filterStatus;
  });

  /**
   * Format a date string
   * @param dateString - The date string to format
   * @returns Formatted date string
   */
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  /**
   * Format a time string
   * @param dateString - The date string to format
   * @returns Formatted time string
   */
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-KE', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /**
   * Format a relative time string
   * @param dateString - The date string to format
   * @returns Formatted relative time string
   */
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    
    return formatDate(dateString);
  };

  /**
   * Get the status color for a shipment
   * @param status - The shipment status
   * @returns The color for the status
   */
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning.main';
      case 'in-transit':
        return 'info.main';
      case 'delivered':
        return 'success.main';
      case 'delayed':
        return 'error.main';
      default:
        return 'text.primary';
    }
  };

  /**
   * Get the status icon for a shipment
   * @param status - The shipment status
   * @returns The icon component for the status
   */
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <TimerIcon sx={{ color: 'warning.main' }} />;
      case 'in-transit':
        return <TruckIcon sx={{ color: 'info.main' }} />;
      case 'delivered':
        return <CheckCircleIcon sx={{ color: 'success.main' }} />;
      case 'delayed':
        return <WarningIcon sx={{ color: 'error.main' }} />;
      default:
        return <InfoIcon />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          <MapIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Shipment Tracking Dashboard
        </Typography>
        
        <Alert severity="info" sx={{ mb: 3 }}>
          Track your shipments in real-time with our enhanced tracking system. Get traffic updates, ETA information, and verification status of drivers and vehicles.
        </Alert>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Enter Tracking Number"
              variant="outlined"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder="e.g. KE12345678"
              InputProps={{
                endAdornment: (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleTrackShipment}
                    disabled={loading}
                    sx={{ ml: 1 }}
                    startIcon={<SearchIcon />}
                  >
                    Track
                  </Button>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Filter by Status"
              >
                <MenuItem value="all">All Shipments</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="in-transit">In Transit</MenuItem>
                <MenuItem value="delivered">Delivered</MenuItem>
                <MenuItem value="delayed">Delayed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}
      </Paper>
      
      <Paper sx={{ p: 0, mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Active Shipments" icon={<TruckIcon />} />
          <Tab label="Delivery History" icon={<HistoryIcon />} />
          <Tab label="Verification Status" icon={<VerifiedUserIcon />} />
        </Tabs>
        
        {/* Active Shipments Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : filteredShipments.length === 0 ? (
              <Alert severity="info">
                No shipments found. Try adjusting your filters or track a specific shipment using the tracking number.
              </Alert>
            ) : (
              <Grid container spacing={3}>
                {filteredShipments.map((shipment) => (
                  <Grid item xs={12} md={6} lg={4} key={shipment.id}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6">
                            {getStatusIcon(shipment.status)}
                            <span style={{ marginLeft: '8px' }}>
                              {shipment.status.charAt(0).toUpperCase() + shipment.status.slice(1)}
                            </span>
                          </Typography>
                          <Chip
                            label={shipment.trackingNumber}
                            color="primary"
                            variant="outlined"
                            size="small"
                          />
                        </Box>
                        
                        <Divider sx={{ mb: 2 }} />
                        
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              <LocationIcon fontSize="small" color="primary" />
                              From
                            </Typography>
                            <Typography variant="body1">
                              {shipment.origin.name}
                            </Typography>
                          </Grid>
                          
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              <LocationIcon fontSize="small" color="secondary" />
                              To
                            </Typography>
                            <Typography variant="body1">
                              {shipment.destination.name}
                            </Typography>
                          </Grid>
                          
                          <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                          </Grid>
                          
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              <CalendarIcon fontSize="small" />
                              Estimated Delivery
                            </Typography>
                            <Typography variant="body1">
                              {formatDate(shipment.estimatedDelivery.date)}
                            </Typography>
                            <Typography variant="body2">
                              {shipment.estimatedDelivery.timeWindow}
                            </Typography>
                          </Grid>
                          
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              <PersonIcon fontSize="small" />
                              Driver
                            </Typography>
                            <Typography variant="body1">
                              {shipment.driver.name}
                              {shipment.driver.verified && (
                                <Tooltip title="Verified Driver">
                                  <CheckCircleIcon 
                                    fontSize="small" 
                                    color="success" 
                                    sx={{ ml: 0.5, verticalAlign: 'middle' }} 
                                  />
                                </Tooltip>
                              )}
                            </Typography>
                            <Typography variant="body2">
                              Rating: {shipment.driver.rating}/5
                            </Typography>
                          </Grid>
                          
                          {shipment.currentLocation && (
                            <Grid item xs={12}>
                              <Alert severity="info" icon={<MyLocationIcon />} sx={{ mt: 1 }}>
                                <Typography variant="body2">
                                  Current Location: {shipment.currentLocation.address}
                                  <br />
                                  Updated: {formatRelativeTime(shipment.currentLocation.updatedAt)}
                                </Typography>
                              </Alert>
                            </Grid>
                          )}
                          
                          {shipment.trafficUpdates && shipment.trafficUpdates.length > 0 && (
                            <Grid item xs={12}>
                              <Alert 
                                severity={shipment.trafficUpdates[0].impact === 'severe' ? 'error' : 'warning'} 
                                icon={<TrafficIcon />}
                              >
                                <Typography variant="body2">
                                  Traffic: {shipment.trafficUpdates[0].condition.charAt(0).toUpperCase() + shipment.trafficUpdates[0].condition.slice(1)} at {shipment.trafficUpdates[0].location}
                                  <br />
                                  Delay: {shipment.trafficUpdates[0].delayMinutes} minutes
                                </Typography>
                              </Alert>
                            </Grid>
                          )}
                          
                          <Grid item xs={12}>
                            <Button
                              fullWidth
                              variant="contained"
                              color="primary"
                              onClick={() => handleOpenTrackingDialog(shipment)}
                              startIcon={<MapIcon />}
                            >
                              Track Shipment
                            </Button>
                          </Grid>
                          
                          <Grid item xs={12}>
                            <Button
                              fullWidth
                              variant="outlined"
                              color="secondary"
                              onClick={() => handleOpenVerificationDialog(shipment)}
                              startIcon={<VerifiedUserIcon />}
                            >
                              View Verification Status
                            </Button>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}
        
        {/* Delivery History Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : filteredShipments.filter(s => s.status === 'delivered').length === 0 ? (
              <Alert severity="info">
                No delivery history found.
              </Alert>
            ) : (
              <List>
                {filteredShipments
                  .filter(s => s.status === 'delivered')
                  .map((shipment) => (
                    <Paper key={shipment.id} sx={{ mb: 2 }}>
                      <ListItem
                        secondaryAction={
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleOpenTrackingDialog(shipment)}
                          >
                            Details
                          </Button>
                        }
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'success.main' }}>
                            <CheckCircleIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <>
                              <Typography component="span" variant="body1" fontWeight="bold">
                                {shipment.cargo.type}
                              </Typography>
                              <Chip 
                                label={shipment.trackingNumber} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                                sx={{ ml: 1 }}
                              />
                            </>
                          }
                          secondary={
                            <>
                              <Typography component="span" variant="body2" display="block">
                                From: {shipment.origin.name} → To: {shipment.destination.name}
                              </Typography>
                              <Typography component="span" variant="body2" color="text.secondary">
                                Delivered: {shipment.actualDelivery ? formatDate(shipment.actualDelivery.date) + ' at ' + shipment.actualDelivery.time : 'N/A'}
                              </Typography>
                            </>
                          }
                        />
                      </ListItem>
                    </Paper>
                  ))}
              </List>
            )}
          </Box>
        )}
        
        {/* Verification Status Tab */}
        {activeTab === 2 && (
          <Box sx={{ p: 3 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              All transporters and logistics companies are verified for insurance coverage and government compliance before being allowed to transport goods.
            </Alert>
            
            <Grid container spacing={3}>
              {filteredShipments.map((shipment) => (
                <Grid item xs={12} md={6} key={shipment.id}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {shipment.driver.name}
                        {shipment.driver.verified && (
                          <Tooltip title="Verified Driver">
                            <CheckCircleIcon 
                              fontSize="small" 
                              color="success" 
                              sx={{ ml: 0.5, verticalAlign: 'middle' }} 
                            />
                          </Tooltip>
                        )}
                      </Typography>
                      
                      <Divider sx={{ mb: 2 }} />
                      
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Vehicle
                          </Typography>
                          <Typography variant="body1">
                            {shipment.vehicle.type}
                          </Typography>
                          <Typography variant="body2">
                            {shipment.vehicle.registrationNumber}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Shipment
                          </Typography>
                          <Typography variant="body1">
                            {shipment.cargo.type}
                          </Typography>
                          <Typography variant="body2">
                            {shipment.trackingNumber}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" gutterBottom>
                            Verification Status
                          </Typography>
                          
                          <List dense>
                            <ListItem>
                              <ListItemIcon>
                                {shipment.driver.verified ? 
                                  <CheckCircleIcon color="success" /> : 
                                  <CancelIcon color="error" />}
                              </ListItemIcon>
                              <ListItemText 
                                primary="Driver Verification" 
                                secondary={shipment.driver.verified ? "Driver identity verified" : "Driver not verified"} 
                              />
                            </ListItem>
                            
                            <ListItem>
                              <ListItemIcon>
                                {shipment.driver.insuranceVerified ? 
                                  <CheckCircleIcon color="success" /> : 
                                  <CancelIcon color="error" />}
                              </ListItemIcon>
                              <ListItemText 
                                primary="Driver Insurance" 
                                secondary={shipment.driver.insuranceVerified ? "Insurance coverage verified" : "Insurance not verified"} 
                              />
                            </ListItem>
                            
                            <ListItem>
                              <ListItemIcon>
                                {shipment.vehicle.insuranceVerified ? 
                                  <CheckCircleIcon color="success" /> : 
                                  <CancelIcon color="error" />}
                              </ListItemIcon>
                              <ListItemText 
                                primary="Vehicle Insurance" 
                                secondary={shipment.vehicle.insuranceVerified ? "Vehicle insurance verified" : "Insurance not verified"} 
                              />
                            </ListItem>
                          </List>
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Button
                            fullWidth
                            variant="outlined"
                            color="primary"
                            onClick={() => handleOpenVerificationDialog(shipment)}
                            startIcon={<VerifiedUserIcon />}
                          >
                            View Verification Details
                          </Button>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Paper>
      
      {/* Tracking Dialog */}
      <Dialog
        open={trackingDialogOpen}
        onClose={handleCloseTrackingDialog}
        maxWidth="md"
        fullWidth
      >
        {selectedShipment && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6">
                  Tracking Shipment: {selectedShipment.trackingNumber}
                </Typography>
                <Chip
                  label={selectedShipment.status.charAt(0).toUpperCase() + selectedShipment.status.slice(1)}
                  color={selectedShipment.status === 'delivered' ? 'success' : 
                         selectedShipment.status === 'in-transit' ? 'info' : 
                         selectedShipment.status === 'delayed' ? 'error' : 'warning'}
                />
              </Box>
            </DialogTitle>
            
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Paper sx={{ p: 2, height: '400px', position: 'relative' }}>
                    {/* This would be replaced with the actual ShipmentTrackingMap component */}
                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body1" color="text.secondary">
                        Map view would be displayed here using ShipmentTrackingMap component
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Shipment Details
                    </Typography>
                    
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <LocationIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Origin" 
                          secondary={selectedShipment.origin.name} 
                        />
                      </ListItem>
                      
                      <ListItem>
                        <ListItemIcon>
                          <LocationIcon color="secondary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Destination" 
                          secondary={selectedShipment.destination.name} 
                        />
                      </ListItem>
                      
                      <ListItem>
                        <ListItemIcon>
                          <CalendarIcon />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Estimated Delivery" 
                          secondary={
                            `${formatDate(selectedShipment.estimatedDelivery.date)} (${selectedShipment.estimatedDelivery.timeWindow})`
                          } 
                        />
                      </ListItem>
                      
                      {selectedShipment.estimatedDelivery.eta && (
                        <ListItem>
                          <ListItemIcon>
                            <TimeIcon color="info" />
                          </ListItemIcon>
                          <ListItemText 
                            primary="ETA" 
                            secondary={selectedShipment.estimatedDelivery.eta} 
                          />
                        </ListItem>
                      )}
                      
                      {selectedShipment.actualDelivery && (
                        <ListItem>
                          <ListItemIcon>
                            <CheckCircleIcon color="success" />
                          </ListItemIcon>
                          <ListItemText 
                            primary="Delivered" 
                            secondary={
                              `${formatDate(selectedShipment.actualDelivery.date)} at ${selectedShipment.actualDelivery.time}`
                            } 
                          />
                        </ListItem>
                      )}
                    </List>
                  </Paper>
                  
                  <Paper sx={{ p: 2, mt: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Cargo Information
                    </Typography>
                    
                    <List dense>
                      <ListItem>
                        <ListItemText 
                          primary="Type" 
                          secondary={selectedShipment.cargo.type} 
                        />
                      </ListItem>
                      
                      <ListItem>
                        <ListItemText 
                          primary="Quantity" 
                          secondary={selectedShipment.cargo.quantity} 
                        />
                      </ListItem>
                      
                      <ListItem>
                        <ListItemText 
                          primary="Weight" 
                          secondary={selectedShipment.cargo.weight} 
                        />
                      </ListItem>
                      
                      {selectedShipment.cargo.specialHandling && selectedShipment.cargo.specialHandling.length > 0 && (
                        <ListItem>
                          <ListItemText 
                            primary="Special Handling" 
                            secondary={
                              <Box sx={{ mt: 1 }}>
                                {selectedShipment.cargo.specialHandling.map((item, index) => (
                                  <Chip 
                                    key={index} 
                                    label={item} 
                                    size="small" 
                                    sx={{ mr: 0.5, mb: 0.5 }} 
                                  />
                                ))}
                              </Box>
                            } 
                          />
                        </ListItem>
                      )}
                      
                      {selectedShipment.environmentalData && (
                        <ListItem>
                          <ListItemIcon>
                            <TemperatureIcon />
                          </ListItemIcon>
                          <ListItemText 
                            primary="Environmental Conditions" 
                            secondary={
                              `Temperature: ${selectedShipment.environmentalData.temperature}°C, Humidity: ${selectedShipment.environmentalData.humidity}%`
                            } 
                          />
                        </ListItem>
                      )}
                    </List>
                  </Paper>
                </Grid>
                
                <Grid item xs={12}>
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>
                        <RouteIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Route Information
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom>
                            Route Details
                          </Typography>
                          <List dense>
                            <ListItem>
                              <ListItemText 
                                primary="Distance" 
                                secondary={`${selectedShipment.route.distance} km`} 
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemText 
                                primary="Duration" 
                                secondary={`${Math.floor(selectedShipment.route.duration / 60)} hours ${selectedShipment.route.duration % 60} minutes`} 
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemText 
                                primary="Traffic Condition" 
                                secondary={selectedShipment.route.trafficCondition.charAt(0).toUpperCase() + selectedShipment.route.trafficCondition.slice(1)} 
                              />
                            </ListItem>
                          </List>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom>
                            Waypoints
                          </Typography>
                          {selectedShipment.route.waypoints.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              No waypoints available
                            </Typography>
                          ) : (
                            <List dense>
                              {selectedShipment.route.waypoints.map((waypoint, index) => (
                                <ListItem key={index}>
                                  <ListItemIcon>
                                    {waypoint.status === 'departed' ? (
                                      <CheckCircleIcon color="success" fontSize="small" />
                                    ) : waypoint.status === 'arrived' ? (
                                      <CheckIcon color="info" fontSize="small" />
                                    ) : (
                                      <TimerIcon color="warning" fontSize="small" />
                                    )}
                                  </ListItemIcon>
                                  <ListItemText 
                                    primary={waypoint.name} 
                                    secondary={
                                      waypoint.arrivalTime ? 
                                        `Arrived: ${waypoint.arrivalTime}${waypoint.departureTime ? `, Departed: ${waypoint.departureTime}` : ''}` : 
                                        'Pending'
                                    } 
                                  />
                                </ListItem>
                              ))}
                            </List>
                          )}
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                  
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>
                        <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Shipment Updates
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List dense>
                        {selectedShipment.updates.map((update, index) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              {update.type === 'success' ? (
                                <CheckCircleIcon color="success" />
                              ) : update.type === 'warning' ? (
                                <WarningIcon color="warning" />
                              ) : (
                                <InfoIcon color="info" />
                              )}
                            </ListItemIcon>
                            <ListItemText 
                              primary={update.message} 
                              secondary={formatRelativeTime(update.timestamp)} 
                            />
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                  
                  {selectedShipment.trafficUpdates && selectedShipment.trafficUpdates.length > 0 && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>
                          <TrafficIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                          Traffic Updates
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List dense>
                          {selectedShipment.trafficUpdates.map((update, index) => (
                            <ListItem key={index}>
                              <ListItemIcon>
                                <TrafficIcon 
                                  color={
                                    update.impact === 'severe' ? 'error' :
                                    update.impact === 'significant' ? 'warning' :
                                    update.impact === 'minor' ? 'info' : 'action'
                                  } 
                                />
                              </ListItemIcon>
                              <ListItemText 
                                primary={`${update.condition.charAt(0).toUpperCase() + update.condition.slice(1)} traffic at ${update.location}`} 
                                secondary={
                                  `Impact: ${update.impact.charAt(0).toUpperCase() + update.impact.slice(1)}${update.delayMinutes ? `, Delay: ${update.delayMinutes} minutes` : ''} (${formatRelativeTime(update.timestamp)})`
                                } 
                              />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            
            <DialogActions>
              <Button onClick={handleCloseTrackingDialog}>Close</Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => {
                  // This would open the driver contact dialog in a real implementation
                  alert(`Contacting driver: ${selectedShipment.driver.name} at ${selectedShipment.driver.phone}`);
                }}
                startIcon={<PhoneIcon />}
              >
                Contact Driver
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      
      {/* Verification Details Dialog */}
      <Dialog
        open={verificationDialogOpen}
        onClose={handleCloseVerificationDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <VerifiedUserIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Verification Details
        </DialogTitle>
        
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 3 }}>
            All transporters and logistics companies are required to meet our verification standards before being allowed to transport goods.
          </Alert>
          
          <List>
            <ListItem>
              <ListItemIcon>
                {verificationDetails.driverVerified ? 
                  <CheckCircleIcon color="success" /> : 
                  <CancelIcon color="error" />}
              </ListItemIcon>
              <ListItemText 
                primary="Driver Verification" 
                secondary="Driver's identity has been verified through government ID and background checks" 
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                {verificationDetails.driverInsuranceVerified ? 
                  <CheckCircleIcon color="success" /> : 
                  <CancelIcon color="error" />}
              </ListItemIcon>
              <ListItemText 
                primary="Driver Insurance" 
                secondary="Driver has personal insurance coverage as required by regulations" 
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                {verificationDetails.vehicleInsuranceVerified ? 
                  <CheckCircleIcon color="success" /> : 
                  <CancelIcon color="error" />}
              </ListItemIcon>
              <ListItemText 
                primary="Vehicle Insurance" 
                secondary="Vehicle has comprehensive insurance coverage including goods in transit" 
              />
            </ListItem>
          </List>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Our verification process includes checking insurance documentation, government compliance, and vehicle inspection certificates. All documents are verified for authenticity and current validity.
          </Typography>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseVerificationDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerTrackingDashboard;